<template>
  <section class="scroll-accordions" :aria-label="ariaLabel || 'Scroll sections'">
    <!-- Desktop: sticky list + sticky image + scroll segments (only one layout mounted — stable refs) -->
    <div v-if="!isMobile" class="scroll-accordions__inner scroll-accordions__desktop">
      <div class="scroll-accordions__list-wrap">
        <div class="scroll-accordions__list" :style="{ top: stickyTop }">
          <div
            v-for="(item, i) in items"
            :key="'d-' + i"
            class="scroll-accordions__row"
            :class="{ 'is-active': activeIndex === i }"
          >
            <div
              class="scroll-accordions__head tt-up"
              @click="scrollToStep(i)"
            >
              <span class="scroll-accordions__arrow" aria-hidden="true">
                <IconBase w="15" h="15" viewBox="0 0 15 15" :color="arrowColor(i)">
                  <IconArrowDown />
                </IconBase>
              </span>
              <span class="scroll-accordions__title">{{ item.title }}</span>
            </div>
            <p
              v-show="activeIndex === i"
              class="scroll-accordions__desc m-t-sm mute"
            >
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>

      <div class="scroll-accordions__right">
        <div
          class="scroll-accordions__sticky-visual"
          :style="{ top: stickyTop }"
        >
          <div
            class="scroll-accordions__img-field"
            :style="{ backgroundColor: imageBgColor }"
          >
            <div class="scroll-accordions__img-gradient"></div>
            <div v-if="activeItem && activeItem.image" class="scroll-accordions__img-inner">
              <img
                :key="activeIndex"
                :src="activeItem.image"
                :alt="activeItem.imageAlt || ''"
                class="scroll-accordions__cutout-img"
              />
            </div>
          </div>
        </div>
        <div
          v-for="(item, i) in items"
          :key="'seg-' + i"
          ref="segments"
          class="scroll-accordions__segment"
          :style="{ minHeight: segmentMinHeight }"
          :aria-hidden="true"
        />
      </div>
    </div>

    <!-- Mobile: horizontal slideshow — title + right arrow, image, description per slide -->
    <div v-else class="scroll-accordions__mobile">
      <div
        ref="mobileTrack"
        class="scroll-accordions__mobile-track"
        tabindex="0"
        @scroll.passive="onMobileTrackScroll"
        @keydown.left.prevent="goToMobileSlide(activeIndex - 1)"
        @keydown.right.prevent="goToMobileSlide(activeIndex + 1)"
      >
        <article
          v-for="(item, i) in items"
          :key="'slide-' + i"
          class="scroll-accordions__mobile-slide"
          :class="{ 'is-active': activeIndex === i }"
        >
          <div class="scroll-accordions__mobile-slide-head">
            <span class="scroll-accordions__mobile-slide-arrow" aria-hidden="true">
              <IconBase w="15" h="15" viewBox="0 0 15 15" :color="arrowColor(i)">
                <IconArrowRight />
              </IconBase>
            </span>
            <h3 class="scroll-accordions__mobile-slide-title tt-up">{{ item.title }}</h3>
          </div>
          <div
            class="scroll-accordions__img-field scroll-accordions__img-field--mobile"
            :style="{ backgroundColor: imageBgColor }"
          >
            <div class="scroll-accordions__img-gradient"></div>
            <div v-if="mobileImageFor(item)" class="scroll-accordions__img-inner">
              <img
                :src="mobileImageFor(item)"
                :alt="item.imageAlt || ''"
                class="scroll-accordions__cutout-img"
              />
            </div>
          </div>
          <p class="scroll-accordions__mobile-desc">
            {{ item.description }}
          </p>
        </article>
      </div>

      <div class="scroll-accordions__mobile-dots" role="group" aria-label="Slide indicators">
        <div
          v-for="(item, i) in items"
          :key="'dot-' + i"
          type="button"
          class="scroll-accordions__mobile-dot"
          :class="{ 'is-active': activeIndex === i }"
          :aria-label="'Go to slide ' + (i + 1) + ' of ' + items.length"
          :aria-current="activeIndex === i ? 'true' : null"
          @click="goToMobileSlide(i)"
        >
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import IconBase from '@/assets/icons/IconBase.vue'
import IconArrowDown from '@/assets/icons/IconArrowDown.vue'
import IconArrowRight from '@/assets/icons/IconArrowRight.vue'

const MOBILE_MQ = '(max-width: 667px)'

export default {
  name: 'ScrollAccordions',
  components: {
    IconBase,
    IconArrowDown,
    IconArrowRight,
  },
  props: {
    /** Each item: { title, description, image, imageAlt?, imageMob? } — imageMob overrides image on mobile only */
    items: {
      type: Array,
      required: true,
      validator: (arr) =>
        Array.isArray(arr) &&
        arr.length > 0 &&
        arr.every(
          (it) =>
            it &&
            typeof it.title === 'string' &&
            typeof it.description === 'string' &&
            it.image != null &&
            (it.imageAlt == null || typeof it.imageAlt === 'string')
        ),
    },
    segmentMinHeight: {
      type: String,
      default: '85vh',
    },
    stickyTop: {
      type: String,
      default: 'calc(27px + .5vw + 24px)',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    /** Panel behind cutout image (same as Col3ImgText / Col6BigImg) */
    imageBgColor: {
      type: String,
      default: '#1c1c1c',
    },
  },
  data() {
    return {
      activeIndex: 0,
      /** Only one desktop/mobile DOM at a time — drives v-if and avoids bogus segment rects */
      isMobile: false,
      scrollTicking: false,
      _resizeTimer: null,
      _mqListener: null,
      _mobileScrollRaf: null,
    }
  },
  computed: {
    activeItem() {
      return this.items[this.activeIndex] || null
    },
  },
  watch: {
    items: {
      handler() {
        this.activeIndex = 0
        this.$nextTick(() => {
          if (this.isMobile) {
            this.syncMobileSlideScroll(false)
          } else {
            this.updateActiveFromScroll()
          }
        })
      },
      deep: true,
    },
  },
  created() {
    if (typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches) {
      this.isMobile = true
    }
  },
  mounted() {
    this._mq = typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ) : null
    this.isMobile = !!(this._mq && this._mq.matches)
    this._mqListener = () => {
      this.isMobile = !!(this._mq && this._mq.matches)
      this.$nextTick(() => {
        if (this.isMobile) {
          this.syncMobileSlideScroll(false)
        } else {
          this.updateActiveFromScroll()
        }
      })
    }
    if (this._mq && this._mq.addEventListener) {
      this._mq.addEventListener('change', this._mqListener)
    } else if (this._mq && this._mq.addListener) {
      this._mq.addListener(this._mqListener)
    }

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onWindowResize, { passive: true })
    this.$nextTick(() => {
      if (this.isMobile) {
        this.syncMobileSlideScroll(false)
      } else {
        this.updateActiveFromScroll()
      }
    })
  },
  beforeDestroy() {
    if (this._mq && this._mq.removeEventListener) {
      this._mq.removeEventListener('change', this._mqListener)
    } else if (this._mq && this._mq.removeListener) {
      this._mq.removeListener(this._mqListener)
    }
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onWindowResize)
    if (this._mobileScrollRaf) {
      cancelAnimationFrame(this._mobileScrollRaf)
      this._mobileScrollRaf = null
    }
  },
  methods: {
    arrowColor(i) {
      return this.activeIndex === i ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'
    },
    /** Mobile slideshow only — falls back to `image` when `imageMob` is omitted */
    mobileImageFor(item) {
      if (!item || item.image == null) return null
      return item.imageMob != null ? item.imageMob : item.image
    },
    segmentEls() {
      const ref = this.$refs.segments
      if (!ref) return []
      return Array.isArray(ref) ? ref : [ref]
    },
    /** Segments drive active index (desktop only) */
    updateActiveFromScroll() {
      if (this.isMobile) return
      const els = this.segmentEls()
      if (!els.length) return

      const mid = window.innerHeight * 0.5

      for (let i = 0; i < els.length; i++) {
        const r = els[i].getBoundingClientRect()
        if (mid >= r.top && mid <= r.bottom) {
          if (this.activeIndex !== i) this.activeIndex = i
          return
        }
      }

      let best = 0
      let bestDist = Infinity
      els.forEach((el, i) => {
        const r = el.getBoundingClientRect()
        const c = r.top + r.height / 2
        const d = Math.abs(mid - c)
        if (d < bestDist) {
          bestDist = d
          best = i
        }
      })
      if (this.activeIndex !== best) this.activeIndex = best
    },
    onScroll() {
      if (this.isMobile) return
      if (this.scrollTicking) return
      this.scrollTicking = true
      requestAnimationFrame(() => {
        this.scrollTicking = false
        this.updateActiveFromScroll()
      })
    },
    onWindowResize() {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => {
        this.$nextTick(() => {
          if (this.isMobile) {
            this.syncMobileSlideScroll(false)
          } else {
            this.updateActiveFromScroll()
          }
        })
      }, 150)
    },
    scrollToStep(i) {
      const els = this.segmentEls()
      const el = els[i]
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    /** Mobile carousel — use slide width (accounts for track padding) */
    mobileSlideWidth(track) {
      if (!track) return 0
      const slide = track.querySelector('.scroll-accordions__mobile-slide')
      return slide ? slide.offsetWidth : track.clientWidth
    },
    syncMobileSlideScroll(smooth = false) {
      const track = this.$refs.mobileTrack
      if (!track) return
      const w = this.mobileSlideWidth(track)
      if (!w) return
      const idx = Math.max(0, Math.min(this.activeIndex, this.items.length - 1))
      track.scrollTo({
        left: idx * w,
        behavior: smooth ? 'smooth' : 'auto',
      })
    },
    goToMobileSlide(i) {
      const n = this.items.length
      if (n === 0) return
      const next = Math.max(0, Math.min(i, n - 1))
      this.activeIndex = next
      this.$nextTick(() => {
        const track = this.$refs.mobileTrack
        if (!track) return
        const w = this.mobileSlideWidth(track)
        track.scrollTo({
          left: next * w,
          behavior: 'smooth',
        })
      })
    },
    onMobileTrackScroll() {
      if (!this.isMobile) return
      if (this._mobileScrollRaf) return
      this._mobileScrollRaf = requestAnimationFrame(() => {
        this._mobileScrollRaf = null
        const track = this.$refs.mobileTrack
        if (!track) return
        const w = this.mobileSlideWidth(track) || 1
        const i = Math.round(track.scrollLeft / w)
        const clamped = Math.max(0, Math.min(i, this.items.length - 1))
        if (clamped !== this.activeIndex) this.activeIndex = clamped
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";
@import "@/scss/_mixins.scss";

.scroll-accordions {
  width: 100%;
  background-color: #000;
}

.scroll-accordions__desktop {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(280px, 1.15fr);
  gap: 40px;
  align-items: stretch;
  padding: 80px 20px;
  max-width: 1440px;
  margin: 0 auto;
  box-sizing: border-box;
}

.scroll-accordions__list-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  align-self: stretch;
}

.scroll-accordions__list {
  position: sticky;
  display: flex;
  flex-direction: column;
}

.scroll-accordions__row {
  padding-bottom: 40px;

  &:last-child {
    padding-bottom: 0;
  }

  &:not(.is-active) .scroll-accordions__title {
    color: rgba(255, 255, 255, 0.2);
  }

  &.is-active .scroll-accordions__title {
    color: #ffffff;
  }
}

.scroll-accordions__head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: $font;
  font-weight: 400;
  font-size: clamp(1.25rem, 3.5vw, 2.5rem);
  line-height: 1.35;
  letter-spacing: 0.02em;

  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: 4px;
  }
}

.scroll-accordions__arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-top: 0.35em;
}

.scroll-accordions__title {
  transition: color 0.2s ease;
}

.scroll-accordions__desc {
  max-width: 360px;
}

.scroll-accordions__right {
  position: relative;
  min-height: 40vh;
  align-self: stretch;
}

.scroll-accordions__sticky-visual {
  position: sticky;
  z-index: 1;
  width: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
  background: #000;
}

/* Col3ImgText-style image wrapper (right column) */
.scroll-accordions__img-field {
  position: relative;
  min-height: 320px;
  max-height: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.scroll-accordions__img-field--mobile {
  min-height: 450px;
}

/* Match Col3ImgText .col3-img-field--bottom .col3-img-inner — no bottom padding on mobile */
.scroll-accordions__img-field--mobile .scroll-accordions__img-inner {
  align-items: flex-end;
  padding-top: $mg-desktop-xl;
  padding-bottom: 0;
  padding-left: $mg-desktop-xl;
  padding-right: $mg-desktop-xl;
}

.scroll-accordions__img-field--mobile .scroll-accordions__cutout-img {
  object-position: center bottom;
}

.scroll-accordions__img-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  padding: $mg-desktop-xl;
}

.scroll-accordions__cutout-img {
  display: block;
  max-width: 100%;
  max-height: 400px;
  height: auto;
  object-fit: contain;
  object-position: center;
  background: transparent;
}

.scroll-accordions__img-gradient {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 30%;
  min-height: 60px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
  z-index: 0;
  pointer-events: none;
}

.scroll-accordions__segment {
  width: 100%;
  pointer-events: none;
}

/* ——— Mobile: horizontal slideshow ——— */
.scroll-accordions__mobile {
  padding: 48px 0 64px $mg-desktop;
  max-width: 1440px;
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
}

.scroll-accordions__mobile-track {
  display: flex;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  gap: 0;
  padding: 0 16px;
  box-sizing: border-box;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  outline: none;

  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: 4px;
  }
}

.scroll-accordions__mobile-slide {
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  box-sizing: border-box;
  padding: 0 2px;
}

.scroll-accordions__mobile-slide-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 100%;
  margin-bottom: $mg-desktop;
}

.scroll-accordions__mobile-slide-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-family: $font;
  font-weight: 400;
  font-size: clamp(1.25rem, 5vw, 2rem);
  line-height: 1.35;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.2);
  transition: color 0.25s ease;
}

.scroll-accordions__mobile-slide.is-active .scroll-accordions__mobile-slide-title {
  color: #ffffff;
}

.scroll-accordions__mobile-slide-arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.scroll-accordions__mobile-desc {
  margin: 16px 0 0 0;
  padding: 0;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.65);
}

.scroll-accordions__mobile-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
  padding: 0 16px;
}

.scroll-accordions__mobile-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.2s ease;

  &:focus-visible {
    outline: 1px solid rgba(255, 255, 255, 0.5);
    outline-offset: 3px;
  }

  &.is-active {
    background: #ffffff;
    transform: scale(1.15);
  }
}
</style>
